import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import ExcelJS from "exceljs";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import DateRangeFilter, { DEFAULT_PRESETS } from "../../common/DateRangeFilter";
import Dropdown from "../../common/dropDown";
import ReportTable from "../../common/ReportTable/ReportTable";
import { useUserData } from "../../../hooks/useUserData";
import { useQmsAccess } from "./qmsPermissionUtils";
import QmsDocumentPreviewModal from "./QmsDocumentPreviewModal";
import {
  fetchQmsArchivedDocumentsReport,
  fetchQmsComplianceReport,
  fetchQmsDocumentArchivingTrendReport,
  fetchQmsDocumentCreationTrendReport,
  fetchQmsPendingApprovalReport,
  fetchQmsPendingReviewReport,
} from "./qmsService";
import {
  QMS_FILTER_INITIAL,
  QMS_REPORT_DATASET_OPTIONS,
  applyReportFilters,
  buildChartFromPivot,
  buildDocumentReportBuilder,
  buildFilterOptions,
  buildReportSummary,
  defaultTableColumns,
  getComplianceReadStatusDisplay,
  getComplianceTableCellValue,
  getDefaultGroupBy,
  getFieldMeta,
  getReportExportTitle,
  getReportGroupByOptions,
  inferFieldCatalog,
  isComplianceReport,
  matchComplianceReadStatusSearch,
  normalizeDatasetRows,
  runPivotQuery,
} from "./qmsReportingEngine";
import "./QmsReports.scss";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const DATE_PRESETS = DEFAULT_PRESETS;

const DATASET_OPTIONS = QMS_REPORT_DATASET_OPTIONS.map((item) => ({
  value: item.value,
  label: item.label,
}));

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function normalizeMulti(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value && value !== "All") return [value];
  return [];
}

function toCellDisplay(value) {
  if (value == null || value === "") return "—";
  const text = String(value);
  if (!text.includes("\n")) return text;
  return (
    <span className="qms-reports-builder__cell-stack">
      {text.split("\n").map((line) => (
        <span key={line}>{line}</span>
      ))}
    </span>
  );
}

async function fetchReportRows(dataset, userData, filters) {
  const dateRange = {
    preset: filters.dateRange,
    startDate: filters.dateFrom,
    endDate: filters.dateTo,
  };

  if (dataset === "compliance") return fetchQmsComplianceReport(userData);
  if (dataset === "pendingApproval") return fetchQmsPendingApprovalReport(userData, dateRange);
  if (dataset === "pendingReview") return fetchQmsPendingReviewReport(userData, dateRange);
  if (dataset === "documentCreationTrend") {
    return fetchQmsDocumentCreationTrendReport(userData, dateRange);
  }
  if (dataset === "documentArchivingTrend") {
    return fetchQmsDocumentArchivingTrendReport(userData, dateRange);
  }
  if (dataset === "archivedDocuments") return fetchQmsArchivedDocumentsReport(userData, dateRange);
  return [];
}

const QmsReports = () => {
  const { userData } = useUserData();
  const { canQmsComplianceReport } = useQmsAccess();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [rows, setRows] = useState([]);
  const [dataset, setDataset] = useState("compliance");
  const [groupBy, setGroupBy] = useState("staff");
  const [filters, setFilters] = useState(QMS_FILTER_INITIAL);
  const [canvasView, setCanvasView] = useState("pivot");
  const [previewModal, setPreviewModal] = useState({ open: false, row: null });

  const groupByOptions = useMemo(
    () =>
      getReportGroupByOptions(dataset).map((item) => ({
        value: item.value,
        label: item.label,
      })),
    [dataset],
  );

  const builder = useMemo(() => buildDocumentReportBuilder(dataset, groupBy), [dataset, groupBy]);
  const reportTitle = useMemo(() => getReportExportTitle(dataset, groupBy), [dataset, groupBy]);
  const showComplianceFilters = isComplianceReport(dataset);
  const showPendingFilters = dataset === "pendingApproval" || dataset === "pendingReview";
  const showOwnerFilter =
    showPendingFilters ||
    dataset === "documentCreationTrend" ||
    dataset === "documentArchivingTrend" ||
    dataset === "archivedDocuments";

  useEffect(() => {
    if (!userData) return undefined;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const raw = await fetchReportRows(dataset, userData, filters);
        if (cancelled) return;
        setRows(normalizeDatasetRows(dataset, raw));
      } catch (error) {
        if (!cancelled) {
          setRows([]);
          setLoadError(error?.message || "Unable to load report data.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Date is applied client-side for compliance; other datasets refetch on date change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userData,
    dataset,
    dataset === "compliance" ? null : filters.dateRange,
    dataset === "compliance" ? null : filters.dateFrom,
    dataset === "compliance" ? null : filters.dateTo,
  ]);

  const fieldCatalog = useMemo(() => inferFieldCatalog(rows, dataset), [rows, dataset]);

  const filteredRows = useMemo(
    () => applyReportFilters(rows, filters, dataset),
    [rows, filters, dataset],
  );

  const pivot = useMemo(
    () => runPivotQuery(filteredRows, builder, fieldCatalog),
    [filteredRows, builder, fieldCatalog],
  );

  const chartData = useMemo(() => buildChartFromPivot(pivot), [pivot]);

  const reportSummary = useMemo(
    () => buildReportSummary(filteredRows, dataset),
    [filteredRows, dataset],
  );

  const filterOptions = useMemo(() => buildFilterOptions(rows, dataset), [rows, dataset]);

  const tableColumns = useMemo(
    () => defaultTableColumns(fieldCatalog, dataset),
    [fieldCatalog, dataset],
  );

  const getFieldLabel = useCallback(
    (field) => getFieldMeta(fieldCatalog, field)?.label || field,
    [fieldCatalog],
  );

  const openPreviewModal = useCallback((row) => {
    if (!row?.documentIdRef && !row?.documentUrl) return;
    setPreviewModal({ open: true, row });
  }, []);

  const closePreviewModal = useCallback(() => {
    setPreviewModal({ open: false, row: null });
  }, []);

  const renderDocumentIdCell = useCallback(
    (row) => {
      const id = String(row?.documentId || "—").trim() || "—";
      const title = String(row?.documentTitle || "").trim();
      const canPreview = Boolean(row?.documentIdRef || row?.documentUrl);
      const titleNode =
        title && title !== "—" ? (
          canPreview ? (
            <button
              type="button"
              className="report-table__cell-link qms-reports-builder__doc-title-link"
              onClick={() => openPreviewModal(row)}
              title={title}
            >
              {title}
            </button>
          ) : (
            <span className="qms-reports-builder__doc-title">{title}</span>
          )
        ) : (
          <span className="qms-reports-builder__doc-title">—</span>
        );

      return (
        <span className="qms-reports-builder__cell-stack">
          {titleNode}
          <span className="qms-doc-lib__doc-id qms-reports-builder__doc-id">{id}</span>
        </span>
      );
    },
    [openPreviewModal],
  );

  const renderTableCell = useCallback(
    (row, field) => {
      if (field === "documentId") {
        return renderDocumentIdCell(row);
      }
      if (showComplianceFilters) {
        if (field === "readStatus") {
          const meta = getComplianceReadStatusDisplay(row);
          return (
            <span
              className={`qms-reports-builder__status ${meta.isRead ? "is-read" : "is-unread"}`}
            >
              {meta.status}
              {meta.quizNote ? (
                <small className="qms-reports-builder__status-note">{meta.quizNote}</small>
              ) : null}
            </span>
          );
        }
        return toCellDisplay(getComplianceTableCellValue(row, field));
      }
      return toCellDisplay(row?.[field] ?? "—");
    },
    [renderDocumentIdCell, showComplianceFilters],
  );

  const updateMultiFilter = useCallback((key, values) => {
    setFilters((prev) => ({
      ...prev,
      [key]: values.length ? values : "All",
    }));
  }, []);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "staffName" && showComplianceFilters) {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-reports-builder__column-filter is-multiselect">
            <BadgeMultiSelect
              options={filterOptions.staff}
              value={normalizeMulti(filters.staff)}
              onChange={(values) => updateMultiFilter("staff", values)}
              emptyLabel="All staff"
              addLabel="+"
              popoverTitle="Filter by staff"
              popoverSubtitle="Select one or more staff members"
              showSearch
              showSelectAll
              compactSummary
              disabled={options?.disabled}
            />
          </div>
        );
      }

      if (field === "assigneeName" && showPendingFilters) {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-reports-builder__column-filter is-multiselect">
            <BadgeMultiSelect
              options={filterOptions.assignee}
              value={normalizeMulti(filters.assignee)}
              onChange={(values) => updateMultiFilter("assignee", values)}
              emptyLabel="All assignees"
              addLabel="+"
              popoverTitle="Filter by assignee"
              popoverSubtitle="Select one or more assignees"
              showSearch
              showSelectAll
              compactSummary
              disabled={options?.disabled}
            />
          </div>
        );
      }

      if (field === "ownerName" && showOwnerFilter) {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-reports-builder__column-filter is-multiselect">
            <BadgeMultiSelect
              options={filterOptions.owner}
              value={normalizeMulti(filters.owner)}
              onChange={(values) => updateMultiFilter("owner", values)}
              emptyLabel="All owners"
              addLabel="+"
              popoverTitle="Filter by owner"
              popoverSubtitle="Select one or more owners"
              showSearch
              showSelectAll
              compactSummary
              disabled={options?.disabled}
            />
          </div>
        );
      }

      return null;
    },
    [
      filterOptions.assignee,
      filterOptions.owner,
      filterOptions.staff,
      filters.assignee,
      filters.owner,
      filters.staff,
      showComplianceFilters,
      showOwnerFilter,
      showPendingFilters,
      updateMultiFilter,
    ],
  );

  const matchColumnSearch = useCallback(
    (row, field, searchValue) => {
      // Multi-select columns are filtered via sidebar/table BadgeMultiSelect + applyReportFilters.
      if (field === "staffName" || field === "assigneeName" || field === "ownerName") {
        return true;
      }

      const raw = String(searchValue || "").trim();
      if (!raw) return true;

      if (field === "documentId") {
        const haystack = `${row.documentId || ""} ${row.documentTitle || ""}`.toLowerCase();
        return haystack.includes(raw.toLowerCase());
      }

      if (showComplianceFilters) {
        if (field === "readStatus") {
          return matchComplianceReadStatusSearch(row, raw);
        }
        const display = String(getComplianceTableCellValue(row, field) || "").toLowerCase();
        return display.includes(raw.toLowerCase());
      }

      return String(row[field] ?? "")
        .toLowerCase()
        .includes(raw.toLowerCase());
    },
    [showComplianceFilters],
  );

  const exportTableToExcel = useCallback(
    async ({ rows: exportRows, columns }) => {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Report");
      const headers = columns.map((field) => getFieldLabel(field));
      const headerRow = ws.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF243F5A" } };
      });
      exportRows.forEach((row) => {
        ws.addRow(
          columns.map((field) => {
            const value = showComplianceFilters
              ? getComplianceTableCellValue(row, field)
              : row[field];
            return String(value ?? "").replace(/\n/g, " | ");
          }),
        );
      });
      const buf = await wb.xlsx.writeBuffer();
      const safeTitle = String(reportTitle).replace(/[^a-z0-9]+/gi, "_");
      downloadBlob(
        new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `${safeTitle}_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
    },
    [getFieldLabel, reportTitle, showComplianceFilters],
  );

  const exportPivotToExcel = useCallback(async () => {
    const colKeys = pivot.colKeys.length ? pivot.colKeys : ["Total"];
    const rowFieldLabel = getFieldLabel(builder.rows[0]) || "Rows";
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Pivot");
    ws.addRow([rowFieldLabel, ...colKeys]);
    pivot.rowKeys.forEach((rowKey) => {
      const rowMap = pivot.matrix.get(rowKey);
      ws.addRow([rowKey, ...colKeys.map((colKey) => rowMap?.get(colKey) ?? 0)]);
    });
    const buf = await wb.xlsx.writeBuffer();
    const safeTitle = String(reportTitle).replace(/[^a-z0-9]+/gi, "_");
    downloadBlob(
      new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `${safeTitle}_Pivot_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  }, [builder.rows, getFieldLabel, pivot, reportTitle]);

  const barChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { intersect: false } },
      scales: {
        x: { grid: { display: false } },
        y: { beginAtZero: true, ticks: { precision: 0 } },
      },
    }),
    [],
  );

  const selectedStaff = normalizeMulti(filters.staff);
  const selectedRoles = normalizeMulti(filters.role);
  const selectedOwners = normalizeMulti(filters.owner);
  const selectedAssignees = normalizeMulti(filters.assignee);
  const selectedTypes = normalizeMulti(filters.documentType);
  const selectedCategories = normalizeMulti(filters.documentCategory);

  const handleDatasetChange = (option) => {
    const nextDataset = option?.value || "compliance";
    setDataset(nextDataset);
    setGroupBy(getDefaultGroupBy(nextDataset));
    setFilters(QMS_FILTER_INITIAL);
    setCanvasView("pivot");
  };

  if (!userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }

  if (!canQmsComplianceReport) return <Navigate to="/notification-center" replace />;

  return (
    <div className="qms-page qms-reports-builder">
      <div className="qms-reports-builder__layout qms-reports-builder__layout--compliance">
        <aside className="qms-reports-builder__sidebar qms-reports-builder__sidebar--controls">
          <div className="qms-reports-builder__card">
            <div className="qms-reports-builder__head">QMS report</div>
            <div className="qms-reports-builder__body">
              <div className="qms-reports-builder__native-filters">
                <div className="qms-reports-builder__filter-field">
                  <span>Report type</span>
                  <Dropdown
                    options={DATASET_OPTIONS}
                    selected={
                      DATASET_OPTIONS.find((item) => item.value === dataset) || DATASET_OPTIONS[0]
                    }
                    onSelect={handleDatasetChange}
                    showSearch={false}
                  />
                </div>

                <div className="qms-reports-builder__filter-field">
                  <span>Group by</span>
                  <Dropdown
                    options={groupByOptions}
                    selected={
                      groupByOptions.find((item) => item.value === groupBy) || groupByOptions[0]
                    }
                    onSelect={(option) => setGroupBy(option?.value || getDefaultGroupBy(dataset))}
                    showSearch={false}
                  />
                </div>

                <div className="qms-reports-builder__filter-field">
                  <span>Date range</span>
                  <DateRangeFilter
                    countryCode="+44"
                    presetOptions={DATE_PRESETS}
                    value={{
                      preset: filters.dateRange,
                      startDate: filters.dateFrom,
                      endDate: filters.dateTo,
                    }}
                    onChange={(range) =>
                      setFilters((prev) => ({
                        ...prev,
                        dateRange: range.preset,
                        dateFrom: range.startDate,
                        dateTo: range.endDate,
                      }))
                    }
                  />
                </div>

                {showComplianceFilters && (
                  <>
                    <div className="qms-reports-builder__filter-field">
                      <span>Staff</span>
                      <BadgeMultiSelect
                        options={filterOptions.staff}
                        value={selectedStaff}
                        onChange={(values) =>
                          setFilters((prev) => ({
                            ...prev,
                            staff: values.length ? values : "All",
                          }))
                        }
                        emptyLabel="All staff"
                        popoverTitle="Add staff"
                        popoverSubtitle="Filter by staff member"
                      />
                    </div>

                    <div className="qms-reports-builder__filter-field">
                      <span>Role</span>
                      <BadgeMultiSelect
                        options={filterOptions.roles}
                        value={selectedRoles}
                        onChange={(values) =>
                          setFilters((prev) => ({
                            ...prev,
                            role: values.length ? values : "All",
                          }))
                        }
                        emptyLabel="All roles"
                        popoverTitle="Add role"
                        popoverSubtitle="Filter by staff role"
                      />
                    </div>
                  </>
                )}

                {showOwnerFilter && (
                  <div className="qms-reports-builder__filter-field">
                    <span>Owner</span>
                    <BadgeMultiSelect
                      options={filterOptions.owner}
                      value={selectedOwners}
                      onChange={(values) =>
                        setFilters((prev) => ({
                          ...prev,
                          owner: values.length ? values : "All",
                        }))
                      }
                      emptyLabel="All owners"
                      popoverTitle="Add owner"
                      popoverSubtitle="Filter by document owner"
                    />
                  </div>
                )}

                {showPendingFilters && (
                  <div className="qms-reports-builder__filter-field">
                    <span>Assignee</span>
                    <BadgeMultiSelect
                      options={filterOptions.assignee}
                      value={selectedAssignees}
                      onChange={(values) =>
                        setFilters((prev) => ({
                          ...prev,
                          assignee: values.length ? values : "All",
                        }))
                      }
                      emptyLabel="All assignees"
                      popoverTitle="Add assignee"
                      popoverSubtitle="Filter by assignee"
                    />
                  </div>
                )}

                <div className="qms-reports-builder__filter-field">
                  <span>Document type</span>
                  <BadgeMultiSelect
                    options={filterOptions.documentTypes}
                    value={selectedTypes}
                    onChange={(values) =>
                      setFilters((prev) => ({
                        ...prev,
                        documentType: values.length ? values : "All",
                      }))
                    }
                    emptyLabel="All types"
                    popoverTitle="Add type"
                    popoverSubtitle="Filter by document type"
                  />
                </div>

                <div className="qms-reports-builder__filter-field">
                  <span>Category</span>
                  <BadgeMultiSelect
                    options={filterOptions.documentCategories}
                    value={selectedCategories}
                    onChange={(values) =>
                      setFilters((prev) => ({
                        ...prev,
                        documentCategory: values.length ? values : "All",
                      }))
                    }
                    emptyLabel="All categories"
                    popoverTitle="Add category"
                    popoverSubtitle="Filter by document category"
                  />
                </div>

                {showComplianceFilters && (
                  <div className="qms-reports-builder__filter-field">
                    <span>Read status</span>
                    <Dropdown
                      options={[
                        { value: "All", label: "All statuses" },
                        ...filterOptions.readStatuses,
                      ]}
                      selected={{
                        value: filters.readStatus,
                        label: filters.readStatus === "All" ? "All statuses" : filters.readStatus,
                      }}
                      onSelect={(option) =>
                        setFilters((prev) => ({
                          ...prev,
                          readStatus: option?.value || "All",
                        }))
                      }
                      showSearch={false}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>

        <main className="qms-reports-builder__canvas-col">
          <div className="qms-reports-builder__card">
            <div className="qms-reports-builder__head qms-reports-builder__canvas-head">
              <div>
                <strong>{reportTitle}</strong>
              </div>
              <div className="qms-reports-builder__output-actions">
                <span className="qms-reports-builder__row-count">
                  {loading ? "Loading…" : `${filteredRows.length.toLocaleString()} rows`}
                </span>
                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    className={`btn btn-outline-secondary ${canvasView === "table" ? "active" : ""}`}
                    onClick={() => setCanvasView("table")}
                  >
                    Table
                  </button>
                  <button
                    type="button"
                    className={`btn btn-outline-secondary ${canvasView === "pivot" ? "active" : ""}`}
                    onClick={() => setCanvasView("pivot")}
                  >
                    Pivot
                  </button>
                  <button
                    type="button"
                    className={`btn btn-outline-secondary ${canvasView === "chart" ? "active" : ""}`}
                    onClick={() => setCanvasView("chart")}
                  >
                    Chart
                  </button>
                </div>
              </div>
            </div>

            <div className="qms-reports-builder__body">
              {loadError && (
                <div className="alert alert-warning py-2 mb-0" role="alert">
                  {loadError}
                </div>
              )}

              <div className="qms-reports-builder__summary">
                {reportSummary.map((item) => (
                  <div key={item.label} className="qms-reports-builder__summary-card">
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>

              {canvasView === "table" ? (
                <ReportTable
                  rows={filteredRows}
                  fieldOptions={fieldCatalog}
                  defaultColumns={tableColumns}
                  getFieldLabel={getFieldLabel}
                  renderCell={renderTableCell}
                  renderColumnSearch={renderColumnSearch}
                  matchColumnSearch={matchColumnSearch}
                  onExportExcel={exportTableToExcel}
                  loading={loading}
                />
              ) : loading ? (
                <div className="qms-reports-builder__loading">
                  <div className="spinner-border spinner-border-sm" role="status" />
                  <span>Loading report data…</span>
                </div>
              ) : (
                <>

                  {canvasView === "pivot" && (
                    <>
                      <div className="qms-reports-builder__component-toolbar">
                        <button type="button" onClick={exportPivotToExcel} disabled={!pivot.rowKeys.length}>
                          <i className="fas fa-file-excel" aria-hidden="true" />
                          Excel
                        </button>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-sm qms-reports-builder__pivot-table">
                          <thead>
                            <tr>
                              <th>{pivot.rowLabel || reportTitle}</th>
                              {pivot.colKeys.map((colKey) => (
                                <th key={colKey}>{colKey}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {pivot.rowKeys.map((rowKey) => (
                              <tr key={rowKey}>
                                <td className="fw-semibold">{rowKey}</td>
                                {pivot.colKeys.map((colKey) => (
                                  <td key={`${rowKey}-${colKey}`}>
                                    {pivot.matrix.get(rowKey)?.get(colKey) ?? 0}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {canvasView === "chart" && (
                    <div className="qms-reports-builder__chart">
                      {!chartData.labels.length ? (
                        <div className="qms-reports-builder__empty">No chart data for this report.</div>
                      ) : (
                        <div className="qms-reports-builder__chart-canvas">
                          <Bar
                            options={barChartOptions}
                            data={{
                              labels: chartData.labels,
                              datasets: [
                                {
                                  data: chartData.values,
                                  backgroundColor: chartData.colors,
                                  borderRadius: 8,
                                  maxBarThickness: 66,
                                },
                              ],
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      <QmsDocumentPreviewModal
        open={previewModal.open}
        onClose={closePreviewModal}
        documentId={String(previewModal.row?.documentIdRef || "")}
        documentUrl={String(previewModal.row?.documentUrl || "")}
        documentExtension={String(previewModal.row?.documentExtension || "")}
        documentTitle={String(previewModal.row?.documentTitle || "")}
        documentCode={String(previewModal.row?.documentId || "")}
      />
    </div>
  );
};

export default QmsReports;
